import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError, imageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, User, Envelope, Phone, LockKey, ShieldCheck, IdentificationCard, UploadSimple, X, Star, MapPin, Translate, ArrowSquareOut } from "@phosphor-icons/react";
import { PushNotificationSection } from "../components/PushNotificationSection";

export function AccountSettings() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileErr, setProfileErr] = useState("");

  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdErr, setPwdErr] = useState("");

  // Public profile state (Fase 4)
  const [pub, setPub] = useState({ photo_url: null, specialties: [], bio: "", coverage_zones: [], languages: [] });
  const [pubLoading, setPubLoading] = useState(false);
  const [pubErr, setPubErr] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [specInput, setSpecInput] = useState("");
  const [zoneInput, setZoneInput] = useState("");
  const [langInput, setLangInput] = useState("");

  useEffect(() => {
    if (user && user !== false) {
      setProfile({ name: user.name || "", phone: user.phone || "" });
      setPub({
        photo_url: user.photo_url || null,
        specialties: user.specialties || [],
        bio: user.bio || "",
        coverage_zones: user.coverage_zones || [],
        languages: user.languages || [],
      });
    }
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileErr(""); setProfileLoading(true);
    try {
      await api.put("/auth/me", profile);
      await refresh();
      toast.success("Perfil actualizado");
    } catch (err) {
      setProfileErr(formatApiError(err, "No pudimos actualizar el perfil"));
    } finally { setProfileLoading(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwdErr("");
    if (pwd.new_password.length < 6) return setPwdErr("La nueva contraseña debe tener al menos 6 caracteres.");
    if (pwd.new_password !== pwd.confirm) return setPwdErr("Las contraseñas no coinciden.");
    setPwdLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Contraseña actualizada");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      setPwdErr(formatApiError(err, "No pudimos cambiar la contraseña"));
    } finally { setPwdLoading(false); }
  };

  // --- Public profile handlers (Fase 4) ---
  const uploadPhoto = async (file) => {
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPub(p => ({ ...p, photo_url: data.url }));
      toast.success("Foto cargada");
    } catch (err) {
      toast.error(formatApiError(err, "No pudimos cargar la foto"));
    } finally { setUploadingPhoto(false); }
  };
  const addChip = (list, item, setInput) => {
    const t = item.trim();
    if (!t) return list;
    if (list.includes(t)) { setInput(""); return list; }
    setInput("");
    return [...list, t];
  };
  const removeChip = (list, i) => list.filter((_, idx) => idx !== i);

  const savePublicProfile = async (e) => {
    e.preventDefault();
    setPubErr(""); setPubLoading(true);
    try {
      await api.put("/agents/me/profile", pub);
      await refresh();
      toast.success("Perfil público actualizado");
    } catch (err) {
      setPubErr(formatApiError(err, "No pudimos actualizar el perfil público"));
    } finally { setPubLoading(false); }
  };

  if (!user || user === false) return null;

  const publicUrl = user.slug ? `${window.location.origin}/asesor/${user.slug}` : "";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="account-page">
      <Link to="/dashboard" className="text-sm text-[#C8A24A]/70 hover:text-[#C8A24A] inline-flex items-center gap-1" data-testid="account-back-link">
        <ArrowLeft size={12} /> Panel
      </Link>
      <div className="mt-4 mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-[#C8A24A] font-semibold">Cuenta</p>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl mt-2 tracking-tight" data-testid="account-title">
          Configuración
        </h1>
        <p className="text-sm text-[#C8A24A]/70 mt-2">Administra tu identidad y seguridad como asesor.</p>
      </div>

      {/* Identity card */}
      <div className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-6 mb-6" data-testid="account-identity-card">
        <div className="flex items-center gap-4 pb-5 border-b border-[#C8A24A]/10">
          <div className="w-14 h-14 rounded-full bg-[#C8A24A] text-[#111111] flex items-center justify-center font-heading text-2xl font-bold flex-shrink-0">
            {(user.name || "?").split(" ").slice(0, 2).map(s => s[0]).join("").toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="font-heading text-xl truncate">{user.name}</p>
            <p className="text-xs text-[#C8A24A]/70 flex items-center gap-1.5 truncate"><Envelope size={12} /> {user.email}</p>
            <p className="text-xs uppercase tracking-widest text-[#C8A24A]/50 mt-1 font-semibold inline-flex items-center gap-1">
              <ShieldCheck size={11} weight="fill" /> Rol: {user.role}
            </p>
          </div>
        </div>
        <form onSubmit={saveProfile} className="mt-5 grid gap-4" data-testid="account-profile-form">
          <div>
            <Label htmlFor="acc-name" className="text-xs uppercase tracking-widest font-semibold text-[#C8A24A]/80 flex items-center gap-1.5">
              <User size={12} weight="duotone" /> Nombre completo
            </Label>
            <Input
              id="acc-name"
              value={profile.name}
              onChange={(e) => setProfile(p => ({ ...p, name: e.target.value }))}
              className="mt-2"
              required
              data-testid="account-name-input"
            />
          </div>
          <div>
            <Label htmlFor="acc-phone" className="text-xs uppercase tracking-widest font-semibold text-[#C8A24A]/80 flex items-center gap-1.5">
              <Phone size={12} weight="duotone" /> Teléfono / WhatsApp
            </Label>
            <Input
              id="acc-phone"
              value={profile.phone}
              onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))}
              placeholder="+52 55 1234 5678"
              className="mt-2"
              data-testid="account-phone-input"
            />
            <p className="text-xs text-[#C8A24A]/50 mt-1">
              Se usa como tu número de WhatsApp en flyers y en el botón "Contactar por WhatsApp" de tus propiedades. Formato México: 10 dígitos o con lada +52.
            </p>
          </div>
          {profileErr && <p className="text-sm text-destructive" data-testid="account-profile-error">{profileErr}</p>}
          <div>
            <Button
              type="submit"
              disabled={profileLoading}
              className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-11 px-6"
              data-testid="account-save-btn"
            >
              {profileLoading ? "Guardando..." : "Guardar cambios"}
            </Button>
          </div>
        </form>
      </div>

      {/* Push notifications (Fase 12A) */}
      <div className="mb-6">
        <PushNotificationSection />
      </div>

      {/* Public profile card (Fase 4) */}
      <div className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-6 mb-6" data-testid="account-public-profile-card">
        <div className="pb-4 border-b border-[#C8A24A]/10 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold flex items-center gap-1.5">
              <IdentificationCard size={12} weight="duotone" /> Perfil público
            </p>
            <h2 className="font-heading text-2xl mt-1">Tu marca profesional</h2>
            <p className="text-xs text-[#C8A24A]/60 mt-1">Cómo te ven los compradores en tu URL pública.</p>
          </div>
          {user.slug && (
            <a
              href={`/asesor/${user.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="account-view-public-btn"
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-[#C8A24A]/40 text-[#C8A24A] hover:bg-[#C8A24A]/10 text-xs font-semibold"
            >
              <ArrowSquareOut size={14} /> Ver perfil público
            </a>
          )}
        </div>

        {user.slug && (
          <div className="mt-4 flex items-center gap-2 flex-wrap p-3 rounded-lg bg-[#0A0A0A] border border-[#C8A24A]/10">
            <span className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold">Tu URL</span>
            <code className="text-xs text-[#C8A24A] font-mono break-all flex-1" data-testid="account-public-url">{publicUrl}</code>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Enlace copiado"); }}
              data-testid="account-copy-url-btn"
            >
              Copiar
            </Button>
          </div>
        )}

        <form onSubmit={savePublicProfile} className="mt-5 grid gap-5" data-testid="account-public-form">
          {/* Photo */}
          <div>
            <Label className="text-xs uppercase tracking-widest font-semibold text-[#C8A24A]/80">Fotografía profesional</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="w-20 h-20 rounded-2xl overflow-hidden bg-[#0A0A0A] border border-[#C8A24A]/20 flex items-center justify-center flex-shrink-0" data-testid="account-photo-preview">
                {pub.photo_url ? (
                  <img src={imageUrl(pub.photo_url)} alt="foto" className="w-full h-full object-cover" />
                ) : (
                  <User size={28} className="text-[#C8A24A]/30" />
                )}
              </div>
              <div className="flex gap-2">
                <label className="cursor-pointer inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-[#C8A24A]/40 text-[#C8A24A] hover:bg-[#C8A24A]/10 text-xs font-semibold" data-testid="account-photo-upload-label">
                  <UploadSimple size={14} /> {uploadingPhoto ? "Subiendo..." : (pub.photo_url ? "Cambiar" : "Subir foto")}
                  <input type="file" accept="image/*" className="hidden" disabled={uploadingPhoto}
                    onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} data-testid="account-photo-input" />
                </label>
                {pub.photo_url && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setPub(p => ({ ...p, photo_url: null }))}
                    className="h-10 px-3"
                    data-testid="account-photo-remove-btn"
                  >
                    <X size={14} className="mr-1" /> Quitar
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Bio */}
          <div>
            <Label htmlFor="acc-bio" className="text-xs uppercase tracking-widest font-semibold text-[#C8A24A]/80">Biografía</Label>
            <Textarea
              id="acc-bio"
              rows={4}
              value={pub.bio}
              onChange={(e) => setPub(p => ({ ...p, bio: e.target.value.slice(0, 2000) }))}
              placeholder="Cuenta tu experiencia, tus valores y qué te diferencia como asesor."
              className="mt-2"
              data-testid="account-bio-input"
            />
            <p className="text-xs text-[#C8A24A]/50 mt-1 text-right">{pub.bio.length}/2000</p>
          </div>

          {/* Specialties */}
          <ChipsField
            label="Especialidades"
            icon={Star}
            value={pub.specialties}
            input={specInput}
            setInput={setSpecInput}
            testId="account-specialties"
            placeholder="Ej. Residencial, Comercial, Terrenos, Lujo"
            onAdd={() => setPub(p => ({ ...p, specialties: addChip(p.specialties, specInput, setSpecInput) }))}
            onRemove={(i) => setPub(p => ({ ...p, specialties: removeChip(p.specialties, i) }))}
          />

          {/* Coverage zones */}
          <ChipsField
            label="Zonas de cobertura"
            icon={MapPin}
            value={pub.coverage_zones}
            input={zoneInput}
            setInput={setZoneInput}
            testId="account-zones"
            placeholder="Ej. San Pedro, Valle Oriente, Polanco"
            onAdd={() => setPub(p => ({ ...p, coverage_zones: addChip(p.coverage_zones, zoneInput, setZoneInput) }))}
            onRemove={(i) => setPub(p => ({ ...p, coverage_zones: removeChip(p.coverage_zones, i) }))}
          />

          {/* Languages */}
          <ChipsField
            label="Idiomas"
            icon={Translate}
            value={pub.languages}
            input={langInput}
            setInput={setLangInput}
            testId="account-languages"
            placeholder="Ej. Español, Inglés"
            onAdd={() => setPub(p => ({ ...p, languages: addChip(p.languages, langInput, setLangInput) }))}
            onRemove={(i) => setPub(p => ({ ...p, languages: removeChip(p.languages, i) }))}
          />

          {pubErr && <p className="text-sm text-destructive" data-testid="account-public-error">{pubErr}</p>}
          <div>
            <Button
              type="submit"
              disabled={pubLoading}
              className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-11 px-6"
              data-testid="account-public-save-btn"
            >
              {pubLoading ? "Guardando..." : "Guardar perfil público"}
            </Button>
          </div>
        </form>
      </div>

      {/* Password card */}
      <div className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-6 mb-6" data-testid="account-security-card">
        <div className="pb-4 border-b border-[#C8A24A]/10">
          <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold flex items-center gap-1.5">
            <LockKey size={12} weight="duotone" /> Seguridad
          </p>
          <h2 className="font-heading text-2xl mt-1">Cambiar contraseña</h2>
        </div>
        <form onSubmit={changePassword} className="mt-5 grid gap-4" data-testid="account-password-form">
          <div>
            <Label htmlFor="acc-current" className="text-xs uppercase tracking-widest font-semibold text-[#C8A24A]/80">
              Contraseña actual
            </Label>
            <Input
              id="acc-current"
              type="password"
              value={pwd.current_password}
              onChange={(e) => setPwd(p => ({ ...p, current_password: e.target.value }))}
              className="mt-2"
              required
              data-testid="account-current-password"
            />
          </div>
          <div>
            <Label htmlFor="acc-new" className="text-xs uppercase tracking-widest font-semibold text-[#C8A24A]/80">
              Nueva contraseña
            </Label>
            <Input
              id="acc-new"
              type="password"
              minLength={6}
              value={pwd.new_password}
              onChange={(e) => setPwd(p => ({ ...p, new_password: e.target.value }))}
              className="mt-2"
              required
              data-testid="account-new-password"
            />
          </div>
          <div>
            <Label htmlFor="acc-confirm" className="text-xs uppercase tracking-widest font-semibold text-[#C8A24A]/80">
              Confirmar nueva contraseña
            </Label>
            <Input
              id="acc-confirm"
              type="password"
              minLength={6}
              value={pwd.confirm}
              onChange={(e) => setPwd(p => ({ ...p, confirm: e.target.value }))}
              className="mt-2"
              required
              data-testid="account-confirm-password"
            />
          </div>
          {pwdErr && <p className="text-sm text-destructive" data-testid="account-password-error">{pwdErr}</p>}
          <div>
            <Button
              type="submit"
              disabled={pwdLoading}
              className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-11 px-6"
              data-testid="account-change-password-btn"
            >
              {pwdLoading ? "Actualizando..." : "Actualizar contraseña"}
            </Button>
          </div>
        </form>
      </div>

      {/* Danger / session */}
      <div className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-6" data-testid="account-session-card">
        <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold">Sesión</p>
        <h2 className="font-heading text-2xl mt-1">Cerrar sesión</h2>
        <p className="text-sm text-[#C8A24A]/70 mt-2">Cierra la sesión en este dispositivo. Podrás volver a entrar en cualquier momento.</p>
        <Button
          variant="outline"
          onClick={async () => { await logout(); navigate("/"); }}
          className="mt-4 h-11"
          data-testid="account-logout-btn"
        >
          Cerrar sesión
        </Button>
      </div>
    </div>
  );
}

function ChipsField({ label, icon: Icon, value, input, setInput, onAdd, onRemove, placeholder, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest font-semibold text-[#C8A24A]/80 flex items-center gap-1.5">
        {Icon && <Icon size={12} weight="duotone" />} {label}
      </Label>
      <div className="mt-2 flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), onAdd())}
          placeholder={placeholder}
          data-testid={`${testId}-input`}
        />
        <Button type="button" variant="outline" onClick={onAdd} data-testid={`${testId}-add-btn`}>Añadir</Button>
      </div>
      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testId}-list`}>
          {value.map((item, i) => (
            <span key={item + i} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#0A0A0A] border border-[#C8A24A]/30 text-[#C8A24A]/90" data-testid={`${testId}-chip-${i}`}>
              {item}
              <button type="button" onClick={() => onRemove(i)} className="hover:text-[#C8A24A]" aria-label={`Quitar ${item}`} data-testid={`${testId}-remove-${i}`}>
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
